import Icon from '$store/components/ui/Icon.tsx'
import { useSignal } from '@preact/signals'
import { ImageWidget } from 'apps/admin/widgets.ts'
import Image from 'apps/website/components/Image.tsx'
import Button from 'site/components/ui/Button.tsx'
import { useEffect, useMemo, useRef } from 'preact/hooks'

export interface Props {
    /** @title Newsletter */
    newsletter: {
        title: string
        subtitle: string
    }
    /** @title App */
    app: {
        qrCode: ImageWidget
        ios: {
            src: ImageWidget
            href: string
            alt: string
        }
        android: {
            src: ImageWidget
            href: string
            alt: string
        }
    }
}

function Newsletter({ newsletter, app }: Props) {
    const emailInputRef = useRef<HTMLInputElement>(null)
    const LGPDInputRef = useRef<HTMLInputElement>(null)
    const errors = useSignal({ name: '', email: '', LGPD: '' })
    const feedBackMessage = useSignal('')

    // TODO: verificar
    useEffect(() => {
        if (LGPDInputRef.current) {
            LGPDInputRef.current.checked = true
        }
    }, [LGPDInputRef])

    async function onSubmit(e: Event) {
        e.preventDefault()

        const email = emailInputRef.current?.value

        errors.value = { name: '', email: '', LGPD: '' }

        if (!email) {
            errors.value.email = 'Preencha o campo e-mail'
            return
        }

        if (!isEmailValid()) {
            errors.value.email = 'Preencha um e-mail válido'
            return
        }

        if (!LGPDInputRef.current?.checked) {
            errors.value.LGPD = 'Aceite necessário'
            return
        }

        const data = {
            email,
            optin: 'true',
        }

        try {
            const formData = new FormData()

            for (const [key, value] of Object.entries(data)) {
                formData.append(key, value)
            }

            const response = await fetch('https://cloud.news.lojastorra.com.br/integracaossjs', {
                method: 'POST',
                body: formData,
            })

            const responseJson = await response.json()

            if (responseJson.statusCode === 200) {
                feedBackMessage.value = 'Seu e-mail foi cadastrado com sucesso.'
            } else {
                feedBackMessage.value = responseJson.message
            }
        } catch (error) {
            feedBackMessage.value = 'Erro ao cadastrar.'
        }
    }

    function isEmailValid() {
        const email = emailInputRef.current?.value
        return email?.match(/^[^@]+@[^@]+\.[^@]+$/)
    }

    const feedBackMessageFormatted = useMemo(() => {
        if (!feedBackMessage.value) return ''
        return feedBackMessage.value.replace(/<\w*>/g, '').replace(/<\/\w*>/g, '')
    }, [feedBackMessage.value])

    return (
        <div
            class='w-full mt-13 bg-secondary-700 flex flex-col relative pt-[100px] lg:pt-[84px] clip'
            style={{
                clipPath: 'polygon(0 calc((100vw * 100) / 1980),100% 0,100% 100%,0 100%)',
            }}
        >
            <div class='max-w-[1079px] w-full mx-auto flex flex-col gap-9 lg:gap-0 lg:flex-row-reverse items-center px-6 lg:px-0 justify-between -mt-8 lg:mt-3 pb-10'>
                <div class='flex flex-col items-center lg:items-start gap-1 w-full lg:max-w-[420px]'>
                    <div class='flex flex-col lg:flex-row items-center gap-1 lg:gap-2'>
                        <Icon id='Email' width={29} height={20} />
                        <p class='text-white text-[23px] font-lato font-extrabold text-center'>
                            {newsletter.title ?? 'Cadastre-se em nossa Newsletter'}
                        </p>
                    </div>
                    <p class='text-white text-xs lg:text-sm font-lato text-center mb-3'>
                        {newsletter.subtitle ??
                            'Cadastre-se e receba promoções, novidades e ofertas exclusivas.'}
                    </p>
                    {feedBackMessage.value !== ''
                        ? (
                            <div class='w-full flex h-[106px] justify-center items-center'>
                                <span class='text-lg text-white '>{feedBackMessageFormatted}</span>
                            </div>
                        )
                        : (
                            <form onSubmit={onSubmit} class='w-full max-w-[378px] lg:max-w-[440px]'>
                                <div class='w-full relative flex '>
                                    <div class='flex flex-col w-full relative'>
                                        <label
                                            htmlFor='email'
                                            class=' text-white font-lato text-sm mb-2 ml-3'
                                        >
                                            Digite seu email
                                        </label>

                                        <div class='flex h-12 max-h-12'>
                                            <input
                                                id='email'
                                                ref={emailInputRef}
                                                class={`w-full max-w-[266px] h-full px-5 text-xs font-lato outline-none ${
                                                    errors.value.LGPD || errors.value.email
                                                        ? 'border border-primary-700'
                                                        : ''
                                                }`}
                                                placeholder='seuemail@seuemail.com.br'
                                            />
                                            <Button
                                                type='submit'
                                                class='max-w-[112px] h-12 max-h-12 text-white bg-primary-700  hover:bg-primary-500 hover:text-white transition-colors 
											px-6 font-bold font-lato text-center'
                                            >
                                                Cadastrar
                                            </Button>
                                        </div>
                                        <span class='text-danger-700 ml-3 mt-2 text-sm font-lato leading-3'>
                                            {errors.value.email}
                                        </span>
                                    </div>
                                </div>
                                <div class='flex items-center ml-3 lg:ml-0 mt-5'>
                                    <input
                                        id='LGPD'
                                        type='checkbox'
                                        ref={LGPDInputRef}
                                        name='LGPD'
                                        class='relative peer shrink-0 appearance-none w-4 h-4 text-white bg-white checked:bg-success-700'
                                    />
                                    <svg
                                        className='
											absolute 
											w-3 h-3
											ml-[2px]
											hidden peer-checked:block
											pointer-events-none'
                                        xmlns='http://www.w3.org/2000/svg'
                                        viewBox='0 0 24 24'
                                        fill='none'
                                        stroke='white'
                                        stroke-width='4'
                                        stroke-linecap='round'
                                        stroke-linejoin='round'
                                    >
                                        <title>
                                            Permito o recebimento por e-mail de promoções e
                                            novidades das Lojas Torra
                                        </title>
                                        <polyline points='20 6 9 17 4 12' />
                                    </svg>
                                    <label
                                        htmlFor='LGPD'
                                        class='text-xs font-lato text-white pl-1 inline-block max-w-[260px] lg:max-w-full'
                                    >
                                        Permito o recebimento por e-mail de promoções e novidades
                                        das Lojas Torra
                                    </label>
                                </div>
                                {errors.value.LGPD && (
                                    <span class='text-red-500 text-[11px] font-lato leading-3 inline-block pl-4'>
                                        {errors.value.LGPD}
                                    </span>
                                )}
                            </form>
                        )}
                </div>

                <div class='w-full lg:max-w-[500px] flex flex-col lg:flex-row items-center gap-4'>
                    <Image
                        src={app.qrCode}
                        width={122}
                        height={122}
                        alt='Qr code'
                        class='rounded-lg hidden lg:block'
                    />
                    <div class='flex flex-col items-center lg:items-start'>
                        <div class='flex flex-col lg:flex-row items-center gap-2'>
                            <Icon id='Iphone' width={18} height={31} class='text-primary-700' />
                            <span class='font-lato text-2xl font-extrabold text-white'>
                                Baixe o App
                            </span>
                        </div>
                        <span class='font-lato block text-sm text-white mb-[14px]'>
                            Disponível para Android e IOs
                        </span>
                        <div class='flex gap-1'>
                            <a href={app.ios.href}>
                                <Image
                                    src={app.ios.src}
                                    width={140}
                                    height={48}
                                    alt={app.ios.alt}
                                />
                            </a>
                            <a href={app.android.href}>
                                <Image
                                    src={app.android.src}
                                    width={159}
                                    height={48}
                                    alt={app.android.alt}
                                />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Newsletter
